/**
 * Admin feature pack management hooks using TanStack Query
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { adminApi } from '../api/adminApi';
import { invalidateMissingRuntimeConfig } from './useMissingRuntimeConfig';
import type {
  FeaturePackSummary,
  FeaturePackDetail,
  LinkedProject,
  BulkLinkResponse,
} from '../schemas/admin.schemas';

const PACKS_KEY = ['admin-packs'];
const packDetailKey = (packId: string) => ['admin-packs', packId];
const projectPackKey = (projectId: string) => ['admin-project-pack', projectId];

export function useAdminPacks() {
  return useQuery<FeaturePackSummary[]>({
    queryKey: PACKS_KEY,
    queryFn: () => adminApi.listPacks(),
  });
}

export function useAdminPackDetail(packId: string) {
  return useQuery<FeaturePackDetail>({
    queryKey: packDetailKey(packId),
    queryFn: () => adminApi.getPack(packId),
    enabled: packId.length > 0,
  });
}

export function useProjectPack(projectId: string) {
  return useQuery<LinkedProject | null>({
    queryKey: projectPackKey(projectId),
    queryFn: () => adminApi.getProjectPack(projectId),
    enabled: projectId.length > 0,
  });
}

export function useCreatePack() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      name,
      displayName,
      description,
    }: {
      name: string;
      displayName: string;
      description?: string;
    }) => adminApi.createPack(name, displayName, description),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: PACKS_KEY });
      invalidateMissingRuntimeConfig(queryClient);
    },
  });
}

export function useUpdatePack() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      packId,
      displayName,
      description,
    }: {
      packId: string;
      displayName?: string;
      description?: string;
    }) => adminApi.updatePack(packId, displayName, description),
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({
        queryKey: packDetailKey(variables.packId),
      });
      void queryClient.invalidateQueries({ queryKey: PACKS_KEY });
      invalidateMissingRuntimeConfig(queryClient);
    },
  });
}

export function useDeletePack() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (packId: string) => adminApi.deletePack(packId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: PACKS_KEY });
      invalidateMissingRuntimeConfig(queryClient);
    },
  });
}

export function useCopyPack() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      packId,
      name,
      displayName,
    }: {
      packId: string;
      name: string;
      displayName: string;
    }) => adminApi.copyPack(packId, name, displayName),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: PACKS_KEY });
      invalidateMissingRuntimeConfig(queryClient);
    },
  });
}

export function useAddPackItem() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      packId,
      featureId,
      options,
    }: {
      packId: string;
      featureId: string;
      options?: Record<string, unknown>;
    }) => adminApi.addPackItem(packId, featureId, options),
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({
        queryKey: packDetailKey(variables.packId),
      });
      void queryClient.invalidateQueries({ queryKey: PACKS_KEY });
      invalidateMissingRuntimeConfig(queryClient);
    },
  });
}

export function useUpdatePackItem() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      packId,
      itemId,
      options,
    }: {
      packId: string;
      itemId: string;
      options: Record<string, unknown>;
    }) => adminApi.updatePackItem(packId, itemId, options),
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({
        queryKey: packDetailKey(variables.packId),
      });
      void queryClient.invalidateQueries({ queryKey: PACKS_KEY });
      invalidateMissingRuntimeConfig(queryClient);
    },
  });
}

export function useRemovePackItem() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      packId,
      itemId,
    }: {
      packId: string;
      itemId: string;
    }) => adminApi.removePackItem(packId, itemId),
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({
        queryKey: packDetailKey(variables.packId),
      });
      void queryClient.invalidateQueries({ queryKey: PACKS_KEY });
      invalidateMissingRuntimeConfig(queryClient);
    },
  });
}

export function useLinkProjectToPack() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      projectId,
      packId,
      projectName,
    }: {
      projectId: string;
      packId: string;
      projectName: string;
    }) => adminApi.linkProjectToPack(projectId, packId, projectName),
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({
        queryKey: projectPackKey(variables.projectId),
      });
      void queryClient.invalidateQueries({ queryKey: PACKS_KEY });
      invalidateMissingRuntimeConfig(queryClient);
    },
  });
}

export function useDetachProjectFromPack() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (projectId: string) =>
      adminApi.detachProjectFromPack(projectId),
    onSuccess: (_data, projectId) => {
      void queryClient.invalidateQueries({
        queryKey: projectPackKey(projectId),
      });
      void queryClient.invalidateQueries({ queryKey: PACKS_KEY });
      invalidateMissingRuntimeConfig(queryClient);
    },
  });
}

export function useBulkLinkProjects() {
  const queryClient = useQueryClient();

  return useMutation<
    BulkLinkResponse,
    Error,
    {
      packId: string;
      projects: Array<{ project_id: string; project_name: string }>;
    }
  >({
    mutationFn: ({ packId, projects }) =>
      adminApi.bulkLinkProjects(packId, projects),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: PACKS_KEY });
      invalidateMissingRuntimeConfig(queryClient);
    },
  });
}
